/**
 * Centralized error handling utilities for axios requests
 *
 * These utilities ensure that 401 errors are never shown to users as error messages,
 * since they are handled transparently by the global axios interceptor which refreshes
 * the token and retries the request automatically.
 */

import { AxiosError } from 'axios';

/**
 * Handles API errors and only calls the error callback for non-401 errors.
 *
 * 401 errors are handled transparently by the global axios interceptor
 * (see axiosInterceptors.ts), which refreshes the token and retries the request.
 * We should never show 401 errors to users as error messages.
 *
 * @param error - The axios error from a catch block
 * @param errorCallback - Optional callback to invoke for non-401 errors
 *
 * @example
 * axios.get('/api/data')
 *   .then(response => handleSuccess(response))
 *   .catch(error => handleApiError(error, showErrorMessage));
 */
export const handleApiError = (error: any, errorCallback?: Function | ((error: any) => void)) => {
  // Don't call error callback for 401s - they're handled by the axios interceptor
  // The interceptor automatically refreshes the token and retries the request
  if (error.response?.status !== 401 && errorCallback) {
    errorCallback(error);
  }
};

/**
 * Type guard to check if an error is a 401 authentication error
 *
 * @param error - The error to check
 * @returns true if the error is a 401, false otherwise
 */
export const is401Error = (error: any): boolean => {
  return error.response?.status === 401;
};

/**
 * Determines if an error should be shown to the user.
 *
 * Returns false for 401 errors (handled by interceptor), true for all other errors.
 *
 * @param error - The axios error
 * @returns true if the error should be shown to the user
 */
export const shouldShowError = (error: any): boolean => {
  return !is401Error(error);
};
